import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  InputAdornment,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import type { FormValues } from "./MainStepper";
import IlIlceListeAPI from "../../../../../src/apis/OB/Get/IlIlceListeAPI";
import EvlendirmeBasvuruAPI from "../../../../../src/apis/OB/SaveApi/EvlendirmeBasvuruAPI";

const TURKEY_CODE = "+90";

type Option = { value: string; label: string };

const Step4IkametBilgileri: React.FC = () => {
  const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext<FormValues>();
  const step4: any = values.step4 ?? {};
  const step4Errors: any = errors?.step4 ?? {};
  const step4Touched: any = touched?.step4 ?? {};

  const [bayanBelediyeIcinde, setBayanBelediyeIcinde] = useState<boolean>(
    !!values.step4?.bayanBelediyeIcinde
  );
  const [erkekBelediyeIcinde, setErkekBelediyeIcinde] = useState<boolean>(
    !!values.step4?.erkekBelediyeIcinde
  );

  const [meslekOptions, setMeslekOptions] = useState<Option[]>([]);
  const [ogrenimOptions, setOgrenimOptions] = useState<Option[]>([]);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [ilIlceListe, setIlIlceListe] = useState<Array<{ ilIlceKodu: string; ilIlceAdi: string }>>([]);

  useEffect(() => {
    setBayanBelediyeIcinde(!!values.step4?.bayanBelediyeIcinde);
  }, [values.step4?.bayanBelediyeIcinde]);

  useEffect(() => {
    setErkekBelediyeIcinde(!!values.step4?.erkekBelediyeIcinde);
  }, [values.step4?.erkekBelediyeIcinde]);

  useEffect(() => {
    if (values?.step4 && values.step4.bayanCepKod !== TURKEY_CODE) {
      setFieldValue("step4.bayanCepKod", TURKEY_CODE);
    }
    if (values?.step4 && values.step4.erkekCepKod !== TURKEY_CODE) {
      setFieldValue("step4.erkekCepKod", TURKEY_CODE);
    }
    if (step4.bayanCepTelefon && String(step4.bayanCepTelefon).startsWith(TURKEY_CODE)) {
      setFieldValue(
        "step4.bayanCepTelefon",
        String(step4.bayanCepTelefon).replace(/[^0-9]/g, "").replace(/^90/, "")
      );
    }
    if (step4.erkekCepTelefon && String(step4.erkekCepTelefon).startsWith(TURKEY_CODE)) {
      setFieldValue(
        "step4.erkekCepTelefon",
        String(step4.erkekCepTelefon).replace(/[^0-9]/g, "").replace(/^90/, "")
      );
    }

    let mounted = true;
    const loadOptions = async () => {
      setLoadingOptions(true);
      setOptionsError(null);
      try {
        const [meslekRes, ogrenimRes] = await Promise.all([
          EvlendirmeBasvuruAPI.ortMeslekList(),
          EvlendirmeBasvuruAPI.ortOgrenimDurumu(),
        ]);

        const normalize = (raw: any): Option[] => {
          const arr = raw?.data ?? raw ?? [];
          if (!Array.isArray(arr)) return [];
          return arr.map((it: any) => {
            if (it?.KODU !== undefined && it?.ADI !== undefined) {
              return { value: String(it.KODU), label: String(it.ADI) };
            }
            if (it?.id !== undefined && (it?.name || it?.label)) {
              return { value: String(it.id), label: String(it.name ?? it.label) };
            }
            const keys = Object.keys(it || {});
            const v = it[keys[0]] ?? "";
            const l = it[keys[1]] ?? String(v);
            return { value: String(v), label: String(l) };
          });
        };

        if (mounted) {
          setMeslekOptions(normalize(meslekRes));
          setOgrenimOptions(normalize(ogrenimRes));
        }
      } catch (err: any) {
        console.error("Seçenekler yüklenirken hata:", err);
        if (mounted) setOptionsError("Seçenekler yüklenemedi. Lütfen tekrar deneyin.");
      } finally {
        if (mounted) setLoadingOptions(false);
      }
    };

    loadOptions();

    return () => {
      mounted = false;
    };
  }, []);

  const parseDateToDayjs = (val?: string | null) => {
    if (!val) return null;
    const d = dayjs(val);
    return d.isValid() ? d : null;
  };

  const onlyDigits = (s: string) => (s || "").replace(/[^0-9]/g, "");

  const formatPhoneDisplay = (digits?: string | null) => {
    const d = onlyDigits(digits ?? "").slice(0, 10);
    if (!d) return "";
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  };

  useEffect(() => {
    const fetchIlIlce = async () => {
      try {
        IlIlceListeAPI.getIlIlceListe(undefined)
          .then((res) => setIlIlceListe(res.data))
          .catch((err) => console.error(err));
      } catch (error) {
        console.error("İl/İlçe listesi yüklenirken hata oluştu:", error);
      }
    };
    fetchIlIlce();
  }, []);

  // Helper fonksiyonlar - doğru path ile
  const getFieldError = (fieldName: string): boolean => {
    return Boolean(step4Touched[fieldName] && step4Errors[fieldName]);
  };

  const getFieldHelperText = (fieldName: string): string => {
    return step4Touched[fieldName] && step4Errors[fieldName]
      ? String(step4Errors[fieldName])
      : "";
  };

  return (
    <Box>
      {/* BAYAN İKAMET BİLGİLERİ */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Kadın İkamet Bilgileri
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={bayanBelediyeIcinde}
            onChange={(e) => {
              const checked = e.target.checked;
              setBayanBelediyeIcinde(checked);
              setFieldValue("step4.bayanBelediyeIcinde", checked);

              if (checked) {
                setFieldValue("step4.bayanIlKodu", "");
                setFieldValue("step4.bayanSiteApartman", "");
                setFieldValue("step4.bayanCepKod", TURKEY_CODE);
                setFieldTouched?.("step4.bayanIlKodu", false);
                setFieldTouched?.("step4.bayanSiteApartman", false);
              }
            }}
          />
        }
        label="Belediye Sınırları İçinde Mi?"
      />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <TextField
            size='small'
            label="Mahalle Adı"
            required
            fullWidth
            value={step4.bayanMahalleAdi ?? ""}
            onChange={(e) => setFieldValue("step4.bayanMahalleAdi", e.target.value)}
            onBlur={() => setFieldTouched("step4.bayanMahalleAdi", true)}
            error={getFieldError("bayanMahalleAdi")}
            helperText={getFieldHelperText("bayanMahalleAdi")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            size='small'
            label="Cadde Sokak Adı"
            required
            fullWidth
            value={step4.bayanCaddeSokak ?? ""}
            onChange={(e) => setFieldValue("step4.bayanCaddeSokak", e.target.value)}
            onBlur={() => setFieldTouched("step4.bayanCaddeSokak", true)}
            error={getFieldError("bayanCaddeSokak")}
            helperText={getFieldHelperText("bayanCaddeSokak")}
          />
        </Grid>

        {!bayanBelediyeIcinde && (
          <Grid item xs={12} md={6}>
            <TextField
              size='small'
              label="Site Apartman Adı"
              fullWidth
              value={step4.bayanSiteApartman ?? ""}
              onChange={(e) => setFieldValue("step4.bayanSiteApartman", e.target.value)}
            />
          </Grid>
        )}

        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Kapı No"
            required
            fullWidth
            value={step4.bayanKapiNo ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 4);
              setFieldValue("step4.bayanKapiNo", val)
            }}
            onBlur={() => setFieldTouched("step4.bayanKapiNo", true)}
            error={getFieldError("bayanKapiNo")}
            helperText={getFieldHelperText("bayanKapiNo")}
            inputProps={{
              inputMode: "numeric",
              max: 9999,
            }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Alt Kapı No"
            fullWidth
            value={step4.bayanAltKapiNo ?? ""}
            onChange={(e) => setFieldValue("step4.bayanAltKapiNo", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Daire No"
            required
            fullWidth
            value={step4.bayanDaireNo ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 4);
              setFieldValue("step4.bayanDaireNo", val)
            }}
            onBlur={() => setFieldTouched("step4.bayanDaireNo", true)}
            error={getFieldError("bayanDaireNo")}
            helperText={getFieldHelperText("bayanDaireNo")}
            inputProps={{
              inputMode: "numeric",
              max: 9999,
            }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Alt Daire No"
            fullWidth
            value={step4.bayanAltDaireNo ?? ""}
            onChange={(e) => setFieldValue("step4.bayanAltDaireNo", e.target.value)}
          />
        </Grid>

        {!bayanBelediyeIcinde && (
          <Grid item xs={12} md={6}>
            <TextField
              size='small'
              id="step4-bayanIlKodu"
              select
              label="Kadın İl/İlçe *"
              fullWidth
              value={step4.bayanIlKodu ?? ""}
              onChange={(e: any) => setFieldValue("step4.bayanIlKodu", e.target.value)}
              onBlur={() => setFieldTouched("step4.bayanIlKodu", true)}
              error={getFieldError("bayanIlKodu")}
              helperText={getFieldHelperText("bayanIlKodu")}
            >
              <MenuItem value="">Seçiniz</MenuItem>
              {ilIlceListe.map((option) => (
                <MenuItem key={option.ilIlceKodu} value={option.ilIlceKodu}>
                  {option.ilIlceAdi}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <TextField
            size='small'
            label="Cep Telefon"
            required
            fullWidth
            type="tel"
            value={formatPhoneDisplay(step4.bayanCepTelefon ?? "")}
            onChange={(e) => {
              const digits = onlyDigits(e.target.value).slice(0, 10);
              setFieldValue("step4.bayanCepTelefon", digits);
              setFieldValue("step4.bayanCepKod", "+90");
            }}
            onBlur={() => setFieldTouched("step4.bayanCepTelefon", true)}
            error={getFieldError("bayanCepTelefon")}
            helperText={getFieldHelperText("bayanCepTelefon")}
            InputProps={{
              startAdornment: <InputAdornment position="start">+90</InputAdornment>,
            }}
            inputProps={{
              inputMode: "numeric",
              maxLength: 12,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            size='small'
            label="Telefon No"
            fullWidth
            type="tel"
            value={formatPhoneDisplay(step4.bayanTelefon ?? "")}
            onChange={(e) => {
              const digits = onlyDigits(e.target.value).slice(0, 10);
              setFieldValue("step4.bayanTelefon", digits);
            }}
            onBlur={() => setFieldTouched("step4.bayanTelefon", true)}
            error={getFieldError("bayanTelefon")}
            helperText={getFieldHelperText("bayanTelefon")}
            inputProps={{
              inputMode: "numeric",
              maxLength: 12,
            }}
          />
        </Grid>
      </Grid>

      {/* Bayan Önceki Evlilik Bilgileri */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 500 }}>
        Kadın Önceki Evlilik Bilgileri
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Evlilik Sayısı"
            fullWidth
            value={step4.bayanEvlilikSayisi ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 3);
              setFieldValue("step4.bayanEvlilikSayisi", val);
            }}
            onBlur={() => setFieldTouched("step4.bayanEvlilikSayisi", true)}
            error={getFieldError("bayanEvlilikSayisi")}
            helperText={getFieldHelperText("bayanEvlilikSayisi")}
            inputProps={{
              inputMode: "numeric",
              max: 999,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Çocuk Sayısı"
            fullWidth
            value={step4.bayanCocukSayisi ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 3);
              setFieldValue("step4.bayanCocukSayisi", val);
            }}
            onBlur={() => setFieldTouched("step4.bayanCocukSayisi", true)}
            error={getFieldError("bayanCocukSayisi")}
            helperText={getFieldHelperText("bayanCocukSayisi")}
            inputProps={{
              inputMode: "numeric",
              max: 999,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Bak.Yük.Old.çocuk Sayısı"
            fullWidth
            value={step4.bayanBakimCocukSayisi ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 3);
              setFieldValue("step4.bayanBakimCocukSayisi", val);
            }}
            onBlur={() => setFieldTouched("step4.bayanBakimCocukSayisi", true)}
            error={getFieldError("bayanBakimCocukSayisi")}
            helperText={getFieldHelperText("bayanBakimCocukSayisi")}
            inputProps={{
              inputMode: "numeric",
              max: 999,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Son Boşanma Tarihi"
              value={parseDateToDayjs(step4.bayanSonBosanmaTarihi)}
              onChange={(newValue: Dayjs | null) => {
                setFieldValue(
                  "step4.bayanSonBosanmaTarihi",
                  newValue ? newValue.startOf("day").toISOString() : ""
                );
              }}
              slotProps={{
                textField: { fullWidth: true ,size:"small"},
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      {/* Bayan Kişisel Bilgiler */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 500 }}>
        Kadın Kişisel Bilgiler
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Meslek</InputLabel>
            <Select
              label="Meslek"
              value={step4.bayanMeslek ?? ""}
              onChange={(e) => setFieldValue("step4.bayanMeslek", e.target.value)}
            >
              {loadingOptions && <MenuItem value="">Yükleniyor...</MenuItem>}
              {!loadingOptions && optionsError && (
                <MenuItem value="">Seçenek yüklenemedi</MenuItem>
              )}
              {!loadingOptions && !optionsError && meslekOptions.length === 0 && (
                <MenuItem value="">Meslek bulunamadı</MenuItem>
              )}
              {!loadingOptions &&
                meslekOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Öğrenim Durumu</InputLabel>
            <Select
              label="Öğrenim Durumu"
              value={step4.bayanOgrenimDurumu ?? ""}
              onChange={(e) => setFieldValue("step4.bayanOgrenimDurumu", e.target.value)}
              size='small'
            >
              {loadingOptions && <MenuItem value="">Yükleniyor...</MenuItem>}
              {!loadingOptions && optionsError && (
                <MenuItem value="">Seçenek yüklenemedi</MenuItem>
              )}
              {!loadingOptions && !optionsError && ogrenimOptions.length === 0 && (
                <MenuItem value="">Öğrenim durumu bulunamadı</MenuItem>
              )}
              {!loadingOptions &&
                ogrenimOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* ERKEK İKAMET BİLGİLERİ */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Erkek İkamet Bilgileri
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={erkekBelediyeIcinde}
            onChange={(e) => {
              const checked = e.target.checked;
              setErkekBelediyeIcinde(checked);
              setFieldValue("step4.erkekBelediyeIcinde", checked);

              if (checked) {
                setFieldValue("step4.erkekIlKodu", "");
                setFieldValue("step4.erkekSiteApartman", "");
                setFieldValue("step4.erkekCepKod", TURKEY_CODE);
                setFieldTouched?.("step4.erkekIlKodu", false);
                setFieldTouched?.("step4.erkekSiteApartman", false);
              }
            }}
          />
        }
        label="Belediye Sınırları İçinde Mi?"
      />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <TextField
            size='small'
            label="Mahalle Adı"
            required
            fullWidth
            value={step4.erkekMahalleAdi ?? ""}
            onChange={(e) => setFieldValue("step4.erkekMahalleAdi", e.target.value)}
            onBlur={() => setFieldTouched("step4.erkekMahalleAdi", true)}
            error={getFieldError("erkekMahalleAdi")}
            helperText={getFieldHelperText("erkekMahalleAdi")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            size='small'
            label="Cadde Sokak Adı"
            required
            fullWidth
            value={step4.erkekCaddeSokak ?? ""}
            onChange={(e) => setFieldValue("step4.erkekCaddeSokak", e.target.value)}
            onBlur={() => setFieldTouched("step4.erkekCaddeSokak", true)}
            error={getFieldError("erkekCaddeSokak")}
            helperText={getFieldHelperText("erkekCaddeSokak")}
          />
        </Grid>

        {!erkekBelediyeIcinde && (
          <Grid item xs={12} md={6}>
            <TextField
              size='small'
              label="Site Apartman Adı"
              fullWidth
              value={step4.erkekSiteApartman ?? ""}
              onChange={(e) => setFieldValue("step4.erkekSiteApartman", e.target.value)}
            />
          </Grid>
        )}

        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Kapı No"
            required
            fullWidth
            value={step4.erkekKapiNo ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 4);
              setFieldValue("step4.erkekKapiNo", val)
            }}
            onBlur={() => setFieldTouched("step4.erkekKapiNo", true)}
            error={getFieldError("erkekKapiNo")}
            helperText={getFieldHelperText("erkekKapiNo")}
            inputProps={{
              inputMode: "numeric",
              max: 9999,
            }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Alt Kapı No"
            fullWidth
            value={step4.erkekAltKapiNo ?? ""}
            onChange={(e) => setFieldValue("step4.erkekAltKapiNo", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Daire No"
            required
            fullWidth
            value={step4.erkekDaireNo ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 4);
              setFieldValue("step4.erkekDaireNo", val)}}
            onBlur={() => setFieldTouched("step4.erkekDaireNo", true)}
            error={getFieldError("erkekDaireNo")}
            helperText={getFieldHelperText("erkekDaireNo")}
            inputProps={{
              inputMode: "numeric",
              max: 9999,
            }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Alt Daire No"
            fullWidth
            value={step4.erkekAltDaireNo ?? ""}
            onChange={(e) => setFieldValue("step4.erkekAltDaireNo", e.target.value)}
          />
        </Grid>

        {!erkekBelediyeIcinde && (
          <Grid item xs={12} md={6}>
            <TextField
              size='small'
              id="step4-erkekIlKodu"
              select
              label="Erkek İl/İlçe *"
              fullWidth
              value={step4.erkekIlKodu ?? ""}
              onChange={(e: any) => setFieldValue("step4.erkekIlKodu", e.target.value)}
              onBlur={() => setFieldTouched("step4.erkekIlKodu", true)}
              error={getFieldError("erkekIlKodu")}
              helperText={getFieldHelperText("erkekIlKodu")}
            >
              <MenuItem value="">Seçiniz</MenuItem>
              {ilIlceListe.map((option) => (
                <MenuItem key={option.ilIlceKodu} value={option.ilIlceKodu}>
                  {option.ilIlceAdi}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <TextField
            size='small'
            label="Cep Telefon"
            required
            fullWidth
            type="tel"
            value={formatPhoneDisplay(step4.erkekCepTelefon ?? "")}
            onChange={(e) => {
              const digits = onlyDigits(e.target.value).slice(0, 10);
              setFieldValue("step4.erkekCepTelefon", digits);
              setFieldValue("step4.erkekCepKod", "+90");
            }}
            onBlur={() => setFieldTouched("step4.erkekCepTelefon", true)}
            error={getFieldError("erkekCepTelefon")}
            helperText={getFieldHelperText("erkekCepTelefon")}
            InputProps={{
              startAdornment: <InputAdornment position="start">+90</InputAdornment>,
            }}
            inputProps={{
              inputMode: "numeric",
              maxLength: 12,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            size='small'
            label="Telefon No"
            fullWidth
            type="tel"
            value={formatPhoneDisplay(step4.erkekTelefon ?? "")}
            onChange={(e) => {
              const digits = onlyDigits(e.target.value).slice(0, 10);
              setFieldValue("step4.erkekTelefon", digits);
            }}
            onBlur={() => setFieldTouched("step4.erkekTelefon", true)}
            error={getFieldError("erkekTelefon")}
            helperText={getFieldHelperText("erkekTelefon")}
            inputProps={{
              inputMode: "numeric",
              maxLength: 12, // 3-3-4 formatı için
            }}
          />
        </Grid>
      </Grid>

      {/* Erkek Önceki Evlilik Bilgileri */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 500 }}>
        Erkek Önceki Evlilik Bilgileri
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Evlilik Sayısı"
            fullWidth
            value={step4.erkekEvlilikSayisi ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 3);
              setFieldValue("step4.erkekEvlilikSayisi", val);
            }}
            onBlur={() => setFieldTouched("step4.erkekEvlilikSayisi", true)}
            error={getFieldError("erkekEvlilikSayisi")}
            helperText={getFieldHelperText("erkekEvlilikSayisi")}
            inputProps={{
              inputMode: "numeric",
              max: 999,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Çocuk Sayısı"
            fullWidth
            value={step4.erkekCocukSayisi ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 3);
              setFieldValue("step4.erkekCocukSayisi", val);
            }}
            onBlur={() => setFieldTouched("step4.erkekCocukSayisi", true)}
            error={getFieldError("erkekCocukSayisi")}
            helperText={getFieldHelperText("erkekCocukSayisi")}
            inputProps={{
              inputMode: "numeric",
              max: 999,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            size='small'
            label="Bak.Yük.Old.çocuk Sayısı"
            fullWidth
            value={step4.erkekBakimCocukSayisi ?? ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 3);
              setFieldValue("step4.erkekBakimCocukSayisi", val);
            }}
            onBlur={() => setFieldTouched("step4.erkekBakimCocukSayisi", true)}
            error={getFieldError("erkekBakimCocukSayisi")}
            helperText={getFieldHelperText("erkekBakimCocukSayisi")}
            inputProps={{
              inputMode: "numeric",
              max: 999,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Son Boşanma Tarihi"
              value={parseDateToDayjs(step4.erkekSonBosanmaTarihi)}
              onChange={(newValue: Dayjs | null) => {
                setFieldValue(
                  "step4.erkekSonBosanmaTarihi",
                  newValue ? newValue.startOf("day").toISOString() : ""
                );
              }}
              slotProps={{
                textField: { fullWidth: true },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      {/* Erkek Kişisel Bilgiler */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 500 }}>
        Erkek Kişisel Bilgiler
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Meslek</InputLabel>
            <Select
              label="Meslek"
              value={step4.erkekMeslek ?? ""}
              onChange={(e) => setFieldValue("step4.erkekMeslek", e.target.value)}
            >
              {loadingOptions && <MenuItem value="">Yükleniyor...</MenuItem>}
              {!loadingOptions && optionsError && (
                <MenuItem value="">Seçenek yüklenemedi</MenuItem>
              )}
              {!loadingOptions && !optionsError && meslekOptions.length === 0 && (
                <MenuItem value="">Meslek bulunamadı</MenuItem>
              )}
              {!loadingOptions &&
                meslekOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Öğrenim Durumu</InputLabel>
            <Select
              label="Öğrenim Durumu"
              value={step4.erkekOgrenimDurumu ?? ""}
              onChange={(e) => setFieldValue("step4.erkekOgrenimDurumu", e.target.value)}
            >
              {loadingOptions && <MenuItem value="">Yükleniyor...</MenuItem>}
              {!loadingOptions && optionsError && (
                <MenuItem value="">Seçenek yüklenemedi</MenuItem>
              )}
              {!loadingOptions && !optionsError && ogrenimOptions.length === 0 && (
                <MenuItem value="">Öğrenim durumu bulunamadı</MenuItem>
              )}
              {!loadingOptions &&
                ogrenimOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* EVLİLİK SONRASI İKAMET BİLGİLERİ */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Evlilik Sonrası İkamet Bilgileri
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            id="step4-evlilikSonrasiIlKodu"
            select
            label="Evlilik Sonrası İl/İlçe"
            fullWidth
            value={step4.evlilikSonrasiIlKodu ?? ""}
            onChange={(e: any) => setFieldValue("step4.evlilikSonrasiIlKodu", e.target.value)}
          >
            <MenuItem value="">Seçiniz</MenuItem>
            {ilIlceListe.map((option) => (
              <MenuItem key={option.ilIlceKodu} value={option.ilIlceKodu}>
                {option.ilIlceAdi}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            label="Mahalle Adı"
            fullWidth
            value={step4.evlilikSonrasiMahalle ?? ""}
            onChange={(e) => setFieldValue("step4.evlilikSonrasiMahalle", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            label="Cadde Sokak Adı"
            fullWidth
            value={step4.evlilikSonrasiCaddeSokak ?? ""}
            onChange={(e) => setFieldValue("step4.evlilikSonrasiCaddeSokak", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            label="Kapı No"
            fullWidth
            value={step4.evlilikSonrasiKapiNo ?? ""}
            onChange={(e) => setFieldValue("step4.evlilikSonrasiKapiNo", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            label="Alt Kapı No"
            fullWidth
            value={step4.evlilikSonrasiAltKapiNo ?? ""}
            onChange={(e) => setFieldValue("step4.evlilikSonrasiAltKapiNo", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            label="Daire No"
            fullWidth
            value={step4.evlilikSonrasiDaireNo ?? ""}
            onChange={(e) => setFieldValue("step4.evlilikSonrasiDaireNo", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            label="Alt Daire No"
            fullWidth
            value={step4.evlilikSonrasiAltDaireNo ?? ""}
            onChange={(e) => setFieldValue("step4.evlilikSonrasiAltDaireNo", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            label="Telefon No"
            fullWidth
            type="tel"
            value={formatPhoneDisplay(step4.evlilikSonrasiTelefon ?? "")}
            onChange={(e) => {
              const digits = onlyDigits(e.target.value).slice(0, 10);
              setFieldValue("step4.evlilikSonrasiTelefon", digits);
            }}
            inputProps={{
              inputMode: "numeric",
              maxLength: 12,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            size='small'
            label="E-Posta"
            fullWidth
            required
            type="email"
            value={step4.evlilikSonrasiEmail ?? ""}
            error={getFieldError("evlilikSonrasiEmail")}
            helperText={getFieldHelperText("evlilikSonrasiEmail")}
            onBlur={() => setFieldTouched("step4.evlilikSonrasiEmail", true)}
            onChange={(e) => setFieldValue("step4.evlilikSonrasiEmail", e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Step4IkametBilgileri;